const HTML_ESCAPES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

const escapeHtml = value =>
  String(value ?? '').replace(/[&<>"']/g, char => HTML_ESCAPES[char]);

/**
 * Opens a standalone, minimal print window for an 80mm thermal receipt and
 * triggers the browser's print dialog. Printing in a fresh window (instead
 * of hiding the current SPA layout via @media print) sidesteps the host
 * page's own CSS entirely, so nothing but the receipt content can leak into
 * the print job.
 * @param {Object} params
 * @param {string} params.title
 * @param {string} [params.contactLabel]
 * @param {string} [params.contactName]
 * @param {string|number} [params.conversationId]
 * @param {string} params.printedAt
 * @param {Array<{ metaLabel: string, content: string }>} params.messages
 */
export function printOrder({
  title,
  contactLabel,
  contactName,
  conversationId,
  printedAt,
  messages,
}) {
  const printWindow = window.open(
    '',
    '_blank',
    'width=900,height=700,left=100,top=80'
  );
  if (!printWindow) return;

  const rows = messages
    .map(
      message => `
        <div class="msg">
          <p class="meta">${escapeHtml(message.metaLabel)}</p>
          <p class="content">${escapeHtml(message.content)}</p>
        </div>`
    )
    .join('');

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>${escapeHtml(title)}</title>
        <style>
          @page { size: 80mm auto; margin: 0; }
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            line-height: 1.4;
            margin: 0;
            padding: 8px;
            width: 80mm;
          }
          .title { font-size: 14px; font-weight: 700; text-align: center; margin: 0 0 4px; }
          .divider { border: none; border-top: 1px dashed #000; margin: 6px 0; }
          .msg { margin-bottom: 8px; }
          .meta { font-weight: 700; margin: 0; }
          .content { margin: 2px 0 0; white-space: pre-wrap; word-break: break-word; }
        </style>
      </head>
      <body>
        <p class="title">${escapeHtml(title)}</p>
        ${contactName ? `<p>${escapeHtml(contactLabel)}: ${escapeHtml(contactName)}</p>` : ''}
        ${conversationId ? `<p>#${escapeHtml(conversationId)}</p>` : ''}
        <p>${escapeHtml(printedAt)}</p>
        <hr class="divider">
        ${rows}
      </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  printWindow.onload = () => {
    printWindow.print();
    printWindow.close();
  };
}
